// The colour theme: system by default, and the sun/moon button cycles
// light, dark, system. The choice is `data-theme` on the root element,
// which the stylesheet's dark tokens answer to, remembered in localStorage
// under one key.

/** The three choices a person can make: a fixed palette or follow the OS. */
export type Theme = "light" | "dark" | "system";

const KEY = "factory.theme";

/** Reads the remembered theme from localStorage; "system" when nothing is stored or storage is unavailable. */
export function readStoredTheme(): Theme {
  try {
    const t = localStorage.getItem(KEY);
    return t === "light" || t === "dark" ? t : "system";
  } catch {
    return "system";
  }
}

/**
 * Sets `data-theme` on the root element (removes it for "system") and
 * remembers the choice in localStorage. A storage failure keeps the choice
 * for the page only.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
  try {
    if (theme === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    /* the choice lasts for the page */
  }
}

/** Reads the current choice off the root element's `data-theme`; "system" when unset. */
export function currentTheme(): Theme {
  const t = document.documentElement.getAttribute("data-theme");
  return t === "light" || t === "dark" ? t : "system";
}

/**
 * Returns the theme the page is painted in: the choice, or the OS
 * preference when the choice is "system". For code that picks a palette
 * itself rather than inheriting it from the stylesheet.
 */
export function resolvedTheme(): "light" | "dark" {
  const chosen = currentTheme();
  if (chosen !== "system") return chosen;
  return typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Calls `cb` whenever resolvedTheme() may have changed: the choice cycled
 * or the OS preference moved under "system". Returns the unsubscribe.
 */
export function onResolvedThemeChange(cb: () => void): () => void {
  const observer = new MutationObserver(cb);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  const media =
    typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
  media?.addEventListener?.("change", cb);
  return () => {
    observer.disconnect();
    media?.removeEventListener?.("change", cb);
  };
}

/** The next choice in the button's cycle: light, then dark, then system. */
export function nextTheme(theme: Theme): Theme {
  return theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
}

/** Applies the remembered theme. Called once at page entry, before the first paint. */
export function initTheme(): void {
  applyTheme(readStoredTheme());
}
